import React from 'react';
import {useTranslation} from 'react-i18next';
import {ProtectionInfo, ProtectionType} from '../../../incase-domain/presentationProtection';
import AuthMethodItem from './parts/AuthMethodItem';
import PasswordForm from './parts/PasswordForm';
import IdentificationForm from './parts/IdentificationForm';
import SelectedUsersForm from './parts/SelectedUsersForm';

interface PresentationProtectionSettingsFormProps {
    protection: ProtectionInfo;
    setProtection: (data: ProtectionInfo) => void;
    errors: Record<string, unknown>;
    selectedAccounts?: {value: string; label: string}[];
}

export default function ProtectionForm({
    protection,
    setProtection,
    errors,
    selectedAccounts,
}: PresentationProtectionSettingsFormProps): JSX.Element {
    const {t} = useTranslation();

    const isSelectedProtectionType = (types: ProtectionType[]): boolean => protection.types.some((type) => types.includes(type));

    const toggleProtectionType = (type: ProtectionType): void => {
        let updatedTypes = protection.types.includes(type) ? protection.types.filter((t) => t !== type) : [...protection.types, type];

        // if GUEST is removed, also remove IDENTIFICATION and PASSWORD
        if (type === ProtectionType.GUEST && protection.types.includes(ProtectionType.GUEST)) {
            updatedTypes = updatedTypes.filter((t) => t !== ProtectionType.IDENTIFICATION && t !== ProtectionType.PASSWORD);
            setProtection({
                ...protection,
                types: updatedTypes,
                password: '',
                validUntil: '',
            });
            return;
        }

        // if REGISTERED_USERS is removed, also remove SELECTED_USERS
        if (type === ProtectionType.REGISTERED_USERS && protection.types.includes(ProtectionType.REGISTERED_USERS)) {
            updatedTypes = updatedTypes.filter((t) => t !== ProtectionType.SELECTED_USERS);
            setProtection({
                ...protection,
                types: updatedTypes,
                allowedIds: [],
                allowedUsers: [],
            });
            return;
        }

        setProtection({
            ...protection,
            types: updatedTypes,
        });
    };

    return (
        <>
            <div className="l-group u-pt-1">
                <AuthMethodItem
                    label={t('presEditPage.settings.presProtection.guest')}
                    description={t('presEditPage.settings.presProtection.guestDescription')}
                    isSelected={isSelectedProtectionType([ProtectionType.GUEST, ProtectionType.PASSWORD, ProtectionType.IDENTIFICATION])}
                    onClick={() => toggleProtectionType(ProtectionType.GUEST)}
                    dataCy="protection_guest"
                >
                    <>
                        {isSelectedProtectionType([ProtectionType.GUEST, ProtectionType.IDENTIFICATION, ProtectionType.PASSWORD]) && (
                            <IdentificationForm protection={protection} setProtection={setProtection} />
                        )}

                        {isSelectedProtectionType([ProtectionType.GUEST, ProtectionType.IDENTIFICATION, ProtectionType.PASSWORD]) && (
                            <PasswordForm protection={protection} setProtection={setProtection} errors={errors} />
                        )}
                    </>
                </AuthMethodItem>

                <AuthMethodItem
                    label={t('presEditPage.settings.presProtection.nobulaAccount')}
                    description={t('presEditPage.settings.presProtection.nobulaDescription')}
                    isSelected={isSelectedProtectionType([ProtectionType.REGISTERED_USERS, ProtectionType.SELECTED_USERS])}
                    onClick={() => toggleProtectionType(ProtectionType.REGISTERED_USERS)}
                    dataCy="protection_ncc"
                >
                    {isSelectedProtectionType([ProtectionType.REGISTERED_USERS, ProtectionType.SELECTED_USERS]) && (
                        <SelectedUsersForm protection={protection} setProtection={setProtection} selectedAccounts={selectedAccounts} />
                    )}
                </AuthMethodItem>

                <AuthMethodItem
                    label={t('presEditPage.settings.presProtection.aaiEdu')}
                    description={t('presEditPage.settings.presProtection.aaiEduDescription')}
                    isSelected={isSelectedProtectionType([ProtectionType.AAI_EDU])}
                    onClick={() => toggleProtectionType(ProtectionType.AAI_EDU)}
                    dataCy="protection_aaiEdu"
                />

                <AuthMethodItem
                    label={t('presEditPage.settings.presProtection.zoom')}
                    description={t('presEditPage.settings.presProtection.zoomDescription')}
                    isSelected={isSelectedProtectionType([ProtectionType.ZOOM])}
                    onClick={() => toggleProtectionType(ProtectionType.ZOOM)}
                    dataCy="protection_zoom"
                />

                <AuthMethodItem
                    label={t('presEditPage.settings.presProtection.emedikus')}
                    description={t('presEditPage.settings.presProtection.emedikusDescription')}
                    isSelected={isSelectedProtectionType([ProtectionType.EMEDIKUS])}
                    onClick={() => toggleProtectionType(ProtectionType.EMEDIKUS)}
                    dataCy="protection_emedikus"
                />
            </div>
        </>
    );
}
